import { rd } from '@passionware/monads';
import type { Resource } from '__types__';
import { ArrowUpRight, ChevronUp, UserRoundSearch, X } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import { Collapsible, CollapsibleContent } from 'v5/design-system/Collapsible';
import { ResourceNameWithPronouns } from 'v5/patterns/resource/ResourceNameWithPronouns';
import { UserAvatar } from 'v5/patterns/widgets/user-avatar/UserAvatar';
import { cn } from 'v5/platform/dom/cn';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithGlobalSettingsService } from 'v5/services/internal/GlobalSettingsService/GlobalSettingsService';

const STACK_LIMIT = 3;

export interface ResourceDuplicatesFooterProps
  extends WithServices<[WithGlobalSettingsService]> {
  /** Existing resources whose name matches the one being typed. */
  matches: Resource[];
  /** Name typed in the form; drives the copy, the highlight and exact matches. */
  typedName: string;
  getProfileHref(resource: Resource): string;
  onDismiss(): void;
  defaultExpanded?: boolean;
  className?: string;
}

/**
 * Compact "possible duplicate" strip for the create dialog footer, pinned
 * above the save buttons. Collapsed it is one line with an avatar stack;
 * expanded it slides a list of the matches up over the strip, each with a
 * profile link that opens in a new tab so the draft survives.
 */
export function ResourceDuplicatesFooter({
  matches,
  typedName,
  getProfileHref,
  onDismiss,
  defaultExpanded = false,
  className,
  services
}: ResourceDuplicatesFooterProps) {
  const { t } = useTranslation('resources', {
    keyPrefix: 'ResourceDuplicatesFooter'
  });
  const [expanded, setExpanded] = useState(defaultExpanded);
  const panelId = useId();

  const typedKey = normalizeName(typedName);
  const sorted = useMemo(
    () =>
      [...matches].sort(
        (a, b) =>
          Number(normalizeName(b.full_name) === typedKey) -
          Number(normalizeName(a.full_name) === typedKey)
      ),
    [matches, typedKey]
  );
  const exactCount = sorted.filter(
    match => normalizeName(match.full_name) === typedKey
  ).length;
  const stack = sorted.slice(0, STACK_LIMIT);
  const overflow = sorted.length - stack.length;

  return (
    <Collapsible
      open={expanded}
      onOpenChange={setExpanded}
      className={cn(
        'relative isolate shrink-0 overflow-hidden border-t border-border-attention dark:border-border-attention-emphasis/35',
        'bg-bg-attention dark:bg-bg-attention-emphasis/12',
        'animate-in fade-in-0 slide-in-from-bottom-2 duration-300 motion-reduce:animate-none',
        className
      )}
    >
      {/* Hazard-tape stripes fading out from the left, same language as the sandbox badge */}
      <div
        aria-hidden
        className={cn(
          'absolute inset-0 -z-10 opacity-10',
          'bg-[repeating-linear-gradient(-45deg,var(--color-fg-attention)_0_8px,transparent_8px_18px)]',
          '[mask-image:linear-gradient(to_right,black,transparent_40%)]'
        )}
      />

      {/* Rendered above the strip so it grows upwards, like a drawer. */}
      <CollapsibleContent id={panelId}>
        <div className="px-6 pt-3">
          <ul className="max-h-60 overflow-y-auto rounded-lg border border-border-attention dark:border-border-attention-emphasis/35 bg-bg-default divide-y divide-border-default shadow-sm">
            {sorted.map(match => {
              const exact = normalizeName(match.full_name) === typedKey;
              const details = [
                match.role,
                match.organisation?.full_name,
                match.emails?.[0]?.value_1
              ].filter(Boolean);
              return (
                <li
                  key={match.id}
                  className="flex items-center gap-3 px-3 py-2.5"
                >
                  <UserAvatar
                    size="sm"
                    user={rd.of({
                      avatarUrl: match.profile_picture,
                      name: match.full_name
                    })}
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <ResourceNameWithPronouns
                      className="truncate text-sm font-semibold text-fg-default"
                      name={
                        <HighlightedName
                          name={match.full_name}
                          query={typedName}
                        />
                      }
                      pronounsOptionId={match.pronouns_option_id}
                      resourceTypeId={match.resource_type_id}
                      services={services}
                    />
                    {details.length > 0 && (
                      <span className="truncate text-xs text-fg-muted">
                        {details.join(' · ')}
                      </span>
                    )}
                  </div>
                  {exact ? (
                    <Badge variant="attention" tone="solid" size="sm">
                      {t('exactMatch')}
                    </Badge>
                  ) : (
                    <Badge variant="gray" tone="subtle" size="sm">
                      {t('similarMatch')}
                    </Badge>
                  )}
                  <Button variant="secondary" size="xs" asChild>
                    <a
                      href={getProfileHref(match)}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t('openProfileOf', {
                        name: match.full_name
                      })}
                    >
                      {t('openProfile')}
                      <ArrowUpRight />
                    </a>
                  </Button>
                </li>
              );
            })}
          </ul>
          <p className="px-1 pt-2 text-xs text-fg-muted">{t('hint')}</p>
        </div>
      </CollapsibleContent>

      <div className="flex items-center gap-2 px-4 py-2">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded(open => !open)}
          className={cn(
            'group flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-1 text-left',
            'transition-colors hover:bg-bg-attention-emphasis/10 focus-visible:outline-2 focus-visible:outline-fg-attention'
          )}
        >
          <EchoIcon count={sorted.length} />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-semibold leading-tight text-fg-default">
              {exactCount > 0 ? t('titleExact') : t('title')}
            </span>
            <span
              role="status"
              className="truncate text-xs leading-snug text-fg-muted"
            >
              {typedName.trim() !== ''
                ? t('summaryNamed', {
                    count: sorted.length,
                    name: typedName.trim()
                  })
                : t('summary', { count: sorted.length })}
            </span>
          </span>
          <span
            aria-hidden
            className="flex shrink-0 -space-x-1.5 max-sm:hidden"
          >
            {stack.map(match => (
              <UserAvatar
                key={match.id}
                size="xs"
                className="ring-2 ring-bg-attention dark:ring-bg-default"
                user={rd.of({
                  avatarUrl: match.profile_picture,
                  name: match.full_name
                })}
              />
            ))}
            {overflow > 0 && (
              <span className="relative flex size-6 items-center justify-center rounded-md bg-bg-subtle text-[10px] font-semibold text-fg-muted ring-2 ring-bg-attention dark:ring-bg-default">
                +{overflow}
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-fg-attention">
            {expanded ? t('hide') : t('review')}
            <ChevronUp
              aria-hidden
              className="size-4 transition-transform duration-200 group-aria-expanded:rotate-180"
            />
          </span>
        </button>
        <Button
          variant="ghost"
          size="xs"
          layout="icon"
          onClick={onDismiss}
          aria-label={t('dismiss')}
          title={t('dismiss')}
        >
          <X />
        </Button>
      </div>
    </Collapsible>
  );
}

/** A tilted search tile with its dashed "echo" behind it: the same thing, twice. */
function EchoIcon({ count }: { count: number }) {
  return (
    <span aria-hidden className="relative size-8 shrink-0">
      <span className="absolute inset-0 translate-x-1 rotate-[10deg] rounded-md border border-dashed border-fg-attention/60" />
      <span className="absolute inset-0 flex rotate-[-6deg] items-center justify-center rounded-md bg-bg-attention-emphasis text-white shadow-sm transition-transform duration-200 group-hover:rotate-0">
        <UserRoundSearch className="size-4" strokeWidth={2.25} />
      </span>
      <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-fg-default px-1 text-[10px] font-bold leading-none text-bg-default ring-2 ring-bg-attention dark:ring-bg-default">
        {count}
      </span>
    </span>
  );
}

function HighlightedName({
  name,
  query
}: {
  name: string;
  query: string;
}): ReactNode {
  const tokens = query
    .trim()
    .split(/\s+/)
    .filter(token => token !== '')
    .map(token => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (tokens.length === 0) return name;
  const pattern = new RegExp(`(${tokens.join('|')})`, 'gi');
  return name.split(pattern).map((part, index) =>
    index % 2 === 1 ? (
      <mark
        key={index}
        className="rounded-sm bg-bg-attention-emphasis/25 text-inherit dark:bg-fg-attention/25"
      >
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function normalizeName(name: string | undefined): string {
  return (name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}
